/* eslint no-constant-condition: ["error", { "checkLoops": false }] */
import { call, put, takeLatest, all, fork } from 'redux-saga/effects';

import { captureException } from 'shared/utils/handlerErrors';
import { getNextURLParam, getNextUrl } from 'shared/utils/url';
import { AUTH_TOKEN_KEY, USER_ID_KEY } from 'shared/constants/auth';
import history from 'routes/history';
import iclinic from 'services/iclinic';
import api from 'services/iclinic/api';
import filterTerms from 'state/userInfo/utils';
import { signIn } from 'services/google/auth2';
import { SERVICE_TERMS } from 'state/userInfo/constants';
import {
  AUTHORIZATION,
  LOGIN_WITH_EMAIL,
  LOGIN_WITH_GOOGLE_AUTH2,
  LOGOUT,
} from './types';
import {
  authorize,
  unauthorize,
  setToken,
  removeToken,
  loginWithEmailSuccess,
  loginWithEmailFailure,
  loginWithGoogleAuth2Success,
  loginWithGoogleAuth2Failure,
  showCaptcha,
  hideCaptcha,
} from './actions';

/**
 * Remove token from store and local storage
 */
export function* workerUnauthorize(action) {
  yield put(unauthorize());
  yield put(removeToken());
  localStorage.removeItem(AUTH_TOKEN_KEY);
  let nextURL = process.env.UNAUTHORIZE_URL;

  if (action && action.type !== LOGOUT.MANUAL) {
    nextURL += getNextUrl(window.location);
  }

  if (process.env.MIGRATION === 'true') {
    window.location = nextURL;
  } else {
    yield call([history, history.push], nextURL);
  }
}

export function workerSetTokenManual({ payload }) {
  const { token } = payload;
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

/**
 * Call auth service and save auth token
 */
export function* workerAuthenticate(response) {
  const { data } = response;

  if (data.ticket) {
    localStorage.setItem(AUTH_TOKEN_KEY, data.ticket.token);
    yield put(authorize());
    yield put(setToken(data.ticket.token));
  }

  if (data.user) {
    localStorage.setItem(USER_ID_KEY, data.user.id);
    try {
      localStorage.setItem(
        SERVICE_TERMS,
        JSON.stringify(filterTerms(data.user.service_terms)),
      );
    } catch (error) {
      captureException(error);
    }
  }
  let nextURL = getNextURLParam(window.location.search);

  if (process.env.MIGRATION !== 'true' && !nextURL) {
    // TODO: in future handle by user kind 'p' or 'r'
    nextURL = '/app/dashboard/';
  }

  if (process.env.MIGRATION === 'true') {
    let redirectURL = `/usuarios/auto-login/?auth_token=${data.auth_token}&uid=${data.uid}`;
    if (nextURL) {
      redirectURL += `&next=${nextURL}`;
    }
    window.location.replace(redirectURL);
  } else {
    yield call([history, history.push], nextURL);
  }
}

/**
 * Check if a user is authenticated when doing requests
 */
export function* callAuth(fn, ...args) {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    api.defaults.params.token = token;
    return yield call(fn, ...args);
  } catch (error) {
    if (error.response) {
      if (error.response.status === 401) {
        // logout user
        yield call(workerUnauthorize);
      }
    } else {
      captureException(error);
    }
    throw error;
  }
}

/**
 * Authentication with email and password
 */
export function* workerLoginWithEmail(email, password, captcha) {
  const authResponse = yield callAuth(
    iclinic.auth.loginWithEmail,
    email,
    password,
    captcha,
  );
  yield call(workerAuthenticate, authResponse);
}

/**
 * Call auth service an dispatch success or failure actions
 */
export function* workerLoginWithEmailSubmit({
  payload: { email, password, captcha },
}) {
  try {
    yield call(workerLoginWithEmail, email, password, captcha);
    if (process.env.MIGRATION !== 'true') {
      yield put(loginWithEmailSuccess());
      yield put(hideCaptcha());
    }
  } catch (error) {
    // TODO: handle to always print error messages
    if (error.response && error.response.data) {
      if (error.response.data.message.captcha_required) {
        yield put(showCaptcha());
      } else {
        yield put(hideCaptcha());
      }
      yield put(
        loginWithEmailFailure({ _error: 'Usuário ou senha inválidos' }),
      );
    } else {
      yield put(
        loginWithEmailFailure({ _error: 'Não foi possível autenticar' }),
      );
    }
  }
}

/**
 * Authentication with Google Token
 */
export function* wokerLoginWithGoogleRequest() {
  let signInData;
  try {
    signInData = yield call(signIn);
    // TODO: verificar se existe o token
    const authResponse = yield callAuth(
      iclinic.auth.loginWithGoogle,
      signInData.token,
    );
    yield call(workerAuthenticate, authResponse);
    if (process.env.MIGRATION !== 'true') {
      yield put(loginWithGoogleAuth2Success());
    }
  } catch (error) {
    if (process.env.MIGRATION === 'true') {
      if (error.response && error.response.data) {
        if (error.response.data.message.email) {
          const url = '/usuarios/setup/email/';
          const qs = `?social_via=google&social_token=${signInData.token}&email=${signInData.email}`;
          window.location.href = url + qs;
        } else {
          yield put(
            loginWithGoogleAuth2Failure({
              _error: 'Dados inválidos ao entrar com o Google',
            }),
          );
        }
      } else {
        yield put(
          loginWithGoogleAuth2Failure({
            _error: 'Não foi possivel entrar com o Google',
          }),
        );
      }
    } else {
      yield put(
        loginWithGoogleAuth2Failure({
          _error: 'Não implementado no Webapp',
        }),
      );
    }
  }
}

export function* watchAuthFlow() {
  yield takeLatest(LOGIN_WITH_EMAIL.SUBMIT, workerLoginWithEmailSubmit);
  yield takeLatest(
    LOGIN_WITH_GOOGLE_AUTH2.REQUEST,
    wokerLoginWithGoogleRequest,
  );
  yield takeLatest(LOGOUT.MANUAL, workerUnauthorize);
  yield takeLatest(AUTHORIZATION.SET_TOKEN_MANUAL, workerSetTokenManual);
}

export default function* authSagas() {
  yield all([fork(watchAuthFlow)]);
}
